/**
 * The Automation view's state: flows, their nodes' badges and logs, and the
 * dashboard. Module-level, like lib/deploys.ts: the socket lives in the board
 * store, the view only while it is open. None of the events are replayed, so
 * `loadAutomation()` runs on opening and again when the socket comes back.
 *
 * The flow being edited is the editor's own copy (AutomationView); a flow
 * that arrives here while it has unsaved changes does not replace them.
 */
import { reactive, ref } from "vue";
import { api } from "./api.js";
import type { DashboardWidget, Flow, FlowGraph, NodeLog, NodeStatus } from "@shared/automation.js";

export type FlowListed = Flow & { running?: boolean };

export const flows = ref(new Map<string, FlowListed>());
export const statuses = reactive(new Map<string, NodeStatus>());
export const widgets = ref(new Map<string, DashboardWidget>());
/** Recent log lines by flow, newest last. Only for flows the view has asked about. */
export const logs = reactive(new Map<string, NodeLog[]>());
export const automationLoaded = ref(false);
/** What the editor's Ctrl+C copied: nodes and the wires among them. Here, not in the
 *  canvas, so a copy pastes into another flow (each flow's canvas is its own instance). */
export const flowClipboard = ref<FlowGraph | null>(null);

const LOG_KEEP = 500;

export const statusKey = (flowId: string, nodeId: string): string => `${flowId}:${nodeId}`;
export const widgetKey = statusKey;

let wanted = false;

export async function loadAutomation(): Promise<void> {
  wanted = true;
  const [{ flows: list, statuses: st }, { widgets: w }] = await Promise.all([
    api.get<{ flows: FlowListed[]; statuses: NodeStatus[] }>("/api/automation/flows"),
    api.get<{ widgets: DashboardWidget[] }>("/api/automation/dashboard"),
  ]);
  flows.value = new Map(list.map((f) => [f.id, f]));
  statuses.clear();
  for (const s of st) statuses.set(statusKey(s.flowId, s.nodeId), s);
  widgets.value = new Map(w.map((x) => [widgetKey(x.flowId, x.nodeId), x]));
  for (const flowId of [...logs.keys()]) void loadLogs(flowId);
  automationLoaded.value = true;
}

/** The socket came back: whatever was missed is fetched again, if the view was ever opened. */
export function automationReconnected(): void {
  if (wanted) void loadAutomation().catch(() => {});
}

export async function loadLogs(flowId: string): Promise<void> {
  const { logs: lines } = await api.get<{ logs: NodeLog[] }>(`/api/automation/flows/${flowId}/logs?limit=${LOG_KEEP}`);
  logs.set(flowId, lines);
}

export async function clearLogs(flowId: string, nodeId: string | null): Promise<void> {
  await api.del(`/api/automation/flows/${flowId}/logs${nodeId ? `?node=${encodeURIComponent(nodeId)}` : ""}`);
  const held = logs.get(flowId);
  if (held) logs.set(flowId, nodeId ? held.filter((l) => l.nodeId !== nodeId) : []);
}

export function flowUpserted(flow: Flow): void {
  const held = flows.value.get(flow.id);
  if (held && held.version > flow.version) return;
  const next = new Map(flows.value);
  next.set(flow.id, { ...flow, running: flow.enabled });
  flows.value = next;
}

export function flowRemoved(id: string): void {
  const next = new Map(flows.value);
  next.delete(id);
  flows.value = next;
  logs.delete(id);
  for (const k of [...statuses.keys()]) if (k.startsWith(`${id}:`)) statuses.delete(k);
  const w = new Map(widgets.value);
  for (const k of [...w.keys()]) if (k.startsWith(`${id}:`)) w.delete(k);
  widgets.value = w;
}

export function statusesChanged(list: NodeStatus[]): void {
  for (const s of list) statuses.set(statusKey(s.flowId, s.nodeId), s);
}

export function logsArrived(entries: NodeLog[]): void {
  for (const e of entries) {
    const held = logs.get(e.flowId);
    if (!held) continue;
    held.push(e);
    if (held.length > LOG_KEEP) held.splice(0, held.length - LOG_KEEP);
  }
}

export function widgetChanged(flowId: string, nodeId: string, widget: DashboardWidget | null): void {
  const next = new Map(widgets.value);
  if (widget) next.set(widgetKey(flowId, nodeId), widget);
  else next.delete(widgetKey(flowId, nodeId));
  widgets.value = next;
}

export function signedOutAutomation(): void {
  wanted = false;
  flows.value = new Map();
  statuses.clear();
  widgets.value = new Map();
  logs.clear();
  automationLoaded.value = false;
}
